import { forwardRef, type HTMLAttributes, type ReactNode } from 'react';
import { Inbox, type LucideIcon } from 'lucide-react';
import { cn } from '../../lib/utils';
import { useTranslation } from 'react-i18next';

/**
 * The "nothing here" surface for empty lists, unfiltered results and unopened
 * detail panes. Copy is Arabic and overridable per screen.
 */

export interface EmptyStateProps extends Omit<HTMLAttributes<HTMLDivElement>, 'title'> {
  icon?: LucideIcon;
  title?: string;
  description?: ReactNode;
  /** Label of the primary action; rendered only together with `onAction`. */
  actionLabel?: string;
  onAction?: () => void;
  actionIcon?: LucideIcon;
  /** Full control over the action area — wins over `actionLabel`/`onAction`. */
  action?: ReactNode;
}

export const EmptyState = forwardRef<HTMLDivElement, EmptyStateProps>(
  (
    {
      icon: Icon = Inbox,
      title: titleProp,
      description,
      actionLabel,
      onAction,
      actionIcon: ActionIcon,
      action,
      className,
      ...props
    },
    ref,
  ) => {
    const { t } = useTranslation('common');
    const title = titleProp ?? t('status.empty');

    return (
      <div
        ref={ref}
        className={cn(
          'flex flex-col items-center justify-center gap-2 px-6 py-10 text-center',
          className,
        )}
        {...props}
      >
        <span className="flex h-12 w-12 items-center justify-center rounded-full bg-slate-100 text-slate-400">
          <Icon className="h-6 w-6" />
        </span>
        <p className="text-xs font-bold text-slate-700">{title}</p>
        {description ? (
          <p className="max-w-sm text-[11px] leading-relaxed text-slate-500">{description}</p>
        ) : null}
        {action ??
          (actionLabel && onAction ? (
            <button type="button" className="btn-primary mt-1" onClick={onAction}>
              {ActionIcon ? <ActionIcon className="h-4 w-4" /> : null}
              {actionLabel}
            </button>
          ) : null)}
      </div>
    );
  },
);
EmptyState.displayName = 'EmptyState';
